// EvolutionTimeline.js
// Drop into: frontend/src/components/EvolutionTimeline.js
//
// Usage in MyIntel.js (full mode):
//   import EvolutionTimeline from '../components/EvolutionTimeline';
//   <EvolutionTimeline userId={ownerId} apiBase={api} />
//
// Compact mode shows just the current-archetype + drift chip,
// suitable for profile headers next to a manager name.
//
// Mirrors RivalryCard.js — self-fetching, dark surface, fail-silent.
// Backend: /api/evolution/{user_id}.

import React, { useEffect, useState } from 'react';
import Icon from './Icon';

// ─────────────────────────────────────────────
// Drift status styling
// ─────────────────────────────────────────────
const DRIFT_STYLES = {
  stable: {
    border: '1px solid rgba(107,114,128,0.4)',
    background: 'rgba(107,114,128,0.1)',
    color: '#9ca3af',
    icon: '·',
    label: 'Stable',
  },
  drifting: {
    border: '1px solid rgba(249,115,22,0.4)',
    background: 'rgba(249,115,22,0.1)',
    color: '#fb923c',
    icon: '📈',
    label: 'Drifting',
  },
  shifting: {
    border: '1px solid rgba(168,85,247,0.4)',
    background: 'rgba(168,85,247,0.1)',
    color: '#c084fc',
    icon: '⚡',
    label: 'Shifting',
  },
};

const driftStyle = (status) => DRIFT_STYLES[status] || DRIFT_STYLES.stable;

// ─────────────────────────────────────────────
// Domain bar — matches the visual language of MyIntel.js DomainBar
// but in the dark-surface palette this component uses
// ─────────────────────────────────────────────
function DomainBar({ label, score, change }) {
  const pct = Math.min(100, Math.max(0, (score / 10) * 100));
  const color = score >= 7 ? 'var(--green)' : score >= 4 ? '#60a5fa' : '#fbbf24';
  const changeColor = change > 0.1 ? '#22c55e' : change < -0.1 ? '#ef4444' : 'rgba(255,255,255,0.4)';
  const arrow = change > 0.1 ? '↑' : change < -0.1 ? '↓' : '·';
  return (
    <div style={{ marginBottom: 10 }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          marginBottom: 4,
        }}
      >
        <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.7)' }}>{label}</span>
        <span style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
          {typeof change === 'number' && Math.abs(change) > 0.05 && (
            <span style={{ fontSize: '10px', color: changeColor }}>
              {arrow} {Math.abs(change).toFixed(1)}
            </span>
          )}
          <span
            style={{
              fontSize: '12px',
              fontFamily: 'monospace',
              color,
              fontWeight: 700,
            }}
          >
            {score.toFixed(1)}/10
          </span>
        </span>
      </div>
      <div
        style={{
          height: 5,
          background: 'rgba(255,255,255,0.06)',
          borderRadius: 3,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            height: '100%',
            width: `${pct}%`,
            background: `linear-gradient(90deg, ${color}88, ${color})`,
            borderRadius: 3,
            transition: 'width 0.6s ease',
          }}
        />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// Era card — one entry per (archetype, season) in the timeline.
// Multi-season eras render as separate cards per season; the
// continuation indicator (↪) links them visually.
// ─────────────────────────────────────────────
function EraCard({ era, archetypeMeta }) {
  const meta = archetypeMeta || {};
  const color = meta.color || '#888';
  const icon = meta.icon || '•';
  const isCurrent = era.is_current;
  const isContinuation = era.is_continuation;
  const isContinued = era.is_continued;
  const when = `Week ${era.start_week}–${era.end_week} · ${era.start_season}`;

  return (
    <div
      style={{
        background: 'rgba(255,255,255,0.04)',
        border: isCurrent ? `1px solid ${color}55` : '1px solid rgba(255,255,255,0.08)',
        borderLeft: `3px solid ${color}`,
        borderRadius: 10,
        padding: '12px 14px',
        position: 'relative',
        marginLeft: isContinuation ? 14 : 0,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        {isContinuation && (
          <span
            title="Continuation of prior season's era"
            style={{ color, fontSize: 14, opacity: 0.75 }}
          >↪</span>
        )}
        <span style={{ fontSize: 16 }}>{icon}</span>
        <span style={{ fontSize: 14, fontWeight: 700, color: 'rgba(255,255,255,0.95)' }}>
          {era.archetype}
        </span>
        {isCurrent && (
          <span
            style={{
              fontSize: 9,
              fontWeight: 700,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              color,
              padding: '1px 6px',
              borderRadius: 4,
              background: `${color}22`,
              border: `1px solid ${color}55`,
              marginLeft: 'auto',
            }}
          >
            Current
          </span>
        )}
        {!isCurrent && (
          <span
            style={{
              fontSize: 10,
              color: 'rgba(255,255,255,0.4)',
              marginLeft: 'auto',
            }}
          >
            {era.duration_weeks} wk{era.duration_weeks === 1 ? '' : 's'}
            {isContinued && ' · continues'}
          </span>
        )}
      </div>
      <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginBottom: 4 }}>
        {when}
      </div>
      {meta.tagline && (
        <div
          style={{
            fontSize: 12,
            color: 'rgba(255,255,255,0.6)',
            fontStyle: 'italic',
            lineHeight: 1.4,
          }}
        >
          "{meta.tagline}"
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// Drift banner — surfaces drift status prominently
// ─────────────────────────────────────────────
function DriftBanner({ drift, domainLabels }) {
  const s = driftStyle(drift?.status);
  const message = drift?.message || 'Not enough history yet.';
  const dom = drift?.dominant_domain;
  const delta = drift?.delta || 0;
  return (
    <div
      style={{
        ...s,
        borderRadius: 10,
        padding: '12px 14px',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
      }}
    >
      <span style={{ fontSize: 18, lineHeight: 1, flexShrink: 0 }}>{s.icon}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            color: s.color,
            marginBottom: 2,
          }}
        >
          {s.label}
        </div>
        <div
          style={{
            fontSize: 13,
            color: 'rgba(255,255,255,0.85)',
            lineHeight: 1.4,
          }}
        >
          {message}
        </div>
        {drift?.status === 'drifting' && dom && (
          <div
            style={{
              fontSize: 11,
              color: 'rgba(255,255,255,0.5)',
              marginTop: 4,
            }}
          >
            {(domainLabels || {})[dom] || dom} · {delta > 0 ? '+' : ''}
            {delta.toFixed(1)} pts across the window
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// Confidence chip
// ─────────────────────────────────────────────
function ConfidenceChip({ confidence }) {
  const pct = Math.round((confidence || 0) * 100);
  const label = confidence >= 0.7 ? 'Established' : confidence >= 0.5 ? 'Emerging' : 'Developing';
  const color = confidence >= 0.7 ? '#22c55e' : confidence >= 0.5 ? '#fbbf24' : '#9ca3af';
  return (
    <span
      style={{
        fontSize: 11,
        fontWeight: 600,
        color,
        background: `${color}1a`,
        border: `1px solid ${color}55`,
        borderRadius: 9999,
        padding: '2px 10px',
        whiteSpace: 'nowrap',
      }}
    >
      {label} · {pct}%
    </span>
  );
}

// ─────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────
export default function EvolutionTimeline({
  userId,
  apiBase = '',
  compact = false,
}) {
  const [data, setData] = useState(null);
  const [archetypeMeta, setArchetypeMeta] = useState({});
  const [domainLabels, setDomainLabels] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!userId) return;
    setLoading(true);
    setError(null);
    Promise.all([
      fetch(`${apiBase}/api/evolution/${userId}`).then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      }),
      fetch(`${apiBase}/api/evolution/archetypes`).then((r) => (r.ok ? r.json() : {})),
    ])
      .then(([mgr, catalog]) => {
        setData(mgr);
        setArchetypeMeta(catalog.archetypes || {});
        setDomainLabels(catalog.domain_labels || {});
        setLoading(false);
      })
      .catch((e) => {
        setError(e.message);
        setLoading(false);
      });
  }, [userId, apiBase]);

  if (loading) {
    return (
      <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: 12, padding: '8px 0' }}>
        Loading behavior evolution...
      </div>
    );
  }

  if (error || !data) {
    return null;
  }

  const current = data.current || {};
  const archetype = current.archetype || 'Unclassified';
  const meta = archetypeMeta[archetype] || {};
  const color = meta.color || '#888';
  const icon = meta.icon || '•';
  const drift = data.drift || {};
  // Prefer the per-season-split biography entries; fall back to raw eras
  // for older payloads that haven't been recomputed.
  const eraEntries = (data.biography || [])
    .filter((b) => b.kind === 'era');
  const eras = eraEntries.length > 0 ? eraEntries : (data.eras || []);
  const scores = current.domain_scores || {};
  const change = drift.windowed_change || {};

  // ── Compact mode ──────────────────────────────
  if (compact) {
    const ds = driftStyle(drift.status);
    return (
      <span
        title={`${archetype} · ${ds.label}`}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          padding: '2px 10px',
          borderRadius: 9999,
          fontSize: 11,
          fontWeight: 600,
          color,
          background: `${color}1a`,
          border: `1px solid ${color}55`,
        }}
      >
        <span>{icon}</span>
        <span>{archetype}</span>
        {drift.status === 'drifting' && (
          <span style={{ color: '#fb923c', marginLeft: 4 }}>📈</span>
        )}
        {drift.status === 'shifting' && (
          <span style={{ color: '#c084fc', marginLeft: 4 }}><Icon emoji="⚡" size={14} /></span>
        )}
      </span>
    );
  }

  // ── Full mode ─────────────────────────────────
  return (
    <div
      style={{
        background: 'rgba(255,255,255,0.04)',
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: 16,
        padding: 20,
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h3
          style={{
            margin: 0,
            fontSize: 13,
            fontWeight: 700,
            color: 'rgba(255,255,255,0.5)',
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
          }}
        >
          Behavior Evolution
        </h3>
        <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.25)' }}>
          Updated weekly · 8-week window
        </span>
      </div>

      {/* Current archetype card */}
      <div
        style={{
          background: `${color}10`,
          border: `1px solid ${color}40`,
          borderRadius: 12,
          padding: 16,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 2,
            background: `linear-gradient(90deg, ${color}, transparent)`,
          }}
        />
        <div
          style={{
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            color,
            opacity: 0.8,
            marginBottom: 6,
          }}
        >
          Current Archetype · Week {current.week} {current.season}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 28, lineHeight: 1 }}>{icon}</span>
          <span style={{ fontSize: 20, fontWeight: 700, color, lineHeight: 1.1 }}>
            {archetype}
          </span>
          <ConfidenceChip confidence={current.confidence || 0} />
        </div>
        {meta.tagline && (
          <div
            style={{
              fontSize: 13,
              color: 'rgba(255,255,255,0.7)',
              fontStyle: 'italic',
              lineHeight: 1.5,
            }}
          >
            "{meta.tagline}"
          </div>
        )}
      </div>

      {/* Drift banner */}
      <DriftBanner drift={drift} domainLabels={domainLabels} />

      {/* Domain scores */}
      <div>
        <div
          style={{
            fontSize: 11,
            color: 'rgba(255,255,255,0.3)',
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            marginBottom: 10,
          }}
        >
          Domain Profile
        </div>
        {Object.keys(scores).length === 0 ? (
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.3)' }}>
            No domain data yet.
          </div>
        ) : (
          Object.entries(scores).map(([key, val]) => (
            <DomainBar
              key={key}
              label={domainLabels[key] || key}
              score={val}
              change={change[key]}
            />
          ))
        )}
      </div>

      {/* Era timeline */}
      <div>
        <div
          style={{
            fontSize: 11,
            color: 'rgba(255,255,255,0.3)',
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            marginBottom: 10,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
          }}
        >
          <span>Dynasty Biography</span>
          <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.25)' }}>
            {eras.length} era{eras.length === 1 ? '' : 's'}
          </span>
        </div>
        {eras.length === 0 ? (
          <div
            style={{
              fontSize: 12,
              color: 'rgba(255,255,255,0.3)',
              textAlign: 'center',
              padding: 12,
            }}
          >
            No behavioral history yet.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {eras.map((era, i) => (
              <EraCard
                key={`${era.archetype}-${era.start_week}-${i}`}
                era={era}
                archetypeMeta={archetypeMeta[era.archetype]}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// Named export — compact archetype + drift chip
// ─────────────────────────────────────────────
export function EvolutionChip({ userId, apiBase = '' }) {
  return <EvolutionTimeline userId={userId} apiBase={apiBase} compact />;
}
